import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Input, Dropdown, List, Typography, Space, Button, Empty, Spin } from 'antd';
import { SearchOutlined, HistoryOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import useDebounce from '../../hooks/useDebounce';
import { useLazyGetApplicationsQuery, useLazyGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import { useLazyGetAllRoutesQuery, useLazyGetRouteByIdQuery } from '../../store/api/routesApi';
import {
  useLazyGetAllContractorsQuery,
  useLazyGetCarriersQuery,
  useLazyGetDriversQuery,
  useLazyGetContractorByIdQuery,
  useLazyGetCarrierByIdQuery,
  useLazyGetDriverByIdQuery,
} from '../../store/api/referencesApi';
import { openGlobalSearchHit } from '../../utils/entityRouter';
import {
  runGlobalSearch,
  groupGlobalSearchHits,
  type GlobalSearchHit,
} from '../../utils/globalSearchAggregate';
import { getSearchHistory, addToSearchHistory, clearSearchHistory, SearchHistoryItem } from '../../utils/searchHistory';
import './GlobalSearch.css';

const { Text } = Typography;

const TYPE_EMOJI: Record<GlobalSearchHit['type'], string> = {
  application: '📄',
  route: '🚚',
  counterparty: '🏢',
  carrier: '🚛',
  driver: '👤',
};

const GlobalSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchHit[]>([]);
  const [visible, setVisible] = useState(false);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<any>(null);

  const debouncedQuery = useDebounce(query, 300);

  const [triggerApps] = useLazyGetApplicationsQuery();
  const [triggerAppById] = useLazyGetApplicationByIdQuery();
  const [triggerRoutes] = useLazyGetAllRoutesQuery();
  const [triggerRouteById] = useLazyGetRouteByIdQuery();
  const [triggerContractors] = useLazyGetAllContractorsQuery();
  const [triggerContractorById] = useLazyGetContractorByIdQuery();
  const [triggerCarriers] = useLazyGetCarriersQuery();
  const [triggerCarrierById] = useLazyGetCarrierByIdQuery();
  const [triggerDrivers] = useLazyGetDriversQuery();
  const [triggerDriverById] = useLazyGetDriverByIdQuery();

  useEffect(() => {
    setSearchHistory(getSearchHistory());
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setVisible(true);
      }
      if (e.key === 'Escape') {
        setVisible(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function exec() {
      if (!debouncedQuery.trim()) {
        setResults([]);
        setSearchError(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setSearchError(false);

      try {
        const hits = await runGlobalSearch(debouncedQuery, {
          getApplicationById: (id) => triggerAppById(id),
          getRouteById: (id) => triggerRouteById(id),
          getContractorById: (id) => triggerContractorById(id),
          getCarrierById: (id) => triggerCarrierById(id),
          getDriverById: (id) => triggerDriverById(id),
          getApplications: (args) => triggerApps(args),
          getAllRoutes: (args) => triggerRoutes(args),
          getAllContractors: () => triggerContractors(),
          getCarriers: () => triggerCarriers(),
          getDrivers: () => triggerDrivers(),
        });

        if (!cancelled) {
          setResults(hits);
          addToSearchHistory(debouncedQuery, hits.length);
          setSearchHistory(getSearchHistory());
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Global search error:', e);
          setResults([]);
          setSearchError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void exec();
    return () => {
      cancelled = true;
    };
  }, [
    debouncedQuery,
    triggerApps,
    triggerAppById,
    triggerRoutes,
    triggerRouteById,
    triggerContractors,
    triggerContractorById,
    triggerCarriers,
    triggerCarrierById,
    triggerDrivers,
    triggerDriverById,
  ]);

  const highlightQuery = debouncedQuery.trim();
  const hasActiveSearch = highlightQuery.length > 0;

  const groupedResults = useMemo(
    () => groupGlobalSearchHits(results, highlightQuery),
    [results, highlightQuery]
  );
  const flatNavItems = useMemo(() => groupedResults.flatMap((s) => s.items), [groupedResults]);
  const flatNavKey = useMemo(
    () => flatNavItems.map((h) => `${h.type}:${h.id}`).join('|'),
    [flatNavItems]
  );

  const activeIndexRef = useRef(-1);
  const flatNavRef = useRef(flatNavItems);
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);
  useEffect(() => {
    flatNavRef.current = flatNavItems;
  }, [flatNavItems]);

  useEffect(() => {
    if (!hasActiveSearch || flatNavItems.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(0);
  }, [hasActiveSearch, debouncedQuery, flatNavKey, flatNavItems.length]);

  const handleResultSelect = useCallback(
    (result: GlobalSearchHit) => {
      openGlobalSearchHit(result, navigate, location);
      setVisible(false);
      setQuery('');
      setActiveIndex(-1);
      inputRef.current?.blur();
    },
    [navigate, location]
  );

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setVisible(true);
  };

  const handleHistorySelect = (historyQuery: string) => {
    setQuery(historyQuery);
    setVisible(true);
    inputRef.current?.focus();
  };

  const handleClearHistory = () => {
    clearSearchHistory();
    setSearchHistory([]);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setVisible(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
      return;
    }

    if (!visible || !hasActiveSearch || flatNavItems.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => {
        const len = flatNavRef.current.length;
        if (len === 0) return -1;
        const next = i < 0 ? 0 : i + 1;
        return Math.min(next, len - 1);
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => {
        const len = flatNavRef.current.length;
        if (len === 0) return -1;
        const next = i < 0 ? len - 1 : i - 1;
        return Math.max(next, 0);
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const list = flatNavRef.current;
      const idx = activeIndexRef.current >= 0 ? activeIndexRef.current : 0;
      const hit = list[idx];
      if (hit) handleResultSelect(hit);
    }
  };

  const getNavIndex = useCallback(
    (result: GlobalSearchHit) =>
      flatNavItems.findIndex((h) => h.type === result.type && h.id === result.id),
    [flatNavItems]
  );

  const renderSearchResultItem = (result: GlobalSearchHit) => {
    const navIdx = getNavIndex(result);
    const isActive = navIdx === activeIndex && activeIndex >= 0;

    return (
      <List.Item
        key={`${result.type}-${result.id}`}
        className={`global-search-item${isActive ? ' global-search-item--active' : ''}`}
        style={{
          cursor: 'pointer',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
        }}
        onClick={() => handleResultSelect(result)}
        onMouseEnter={() => setActiveIndex(navIdx)}
      >
        <List.Item.Meta
          avatar={
            <div className="global-search-icon global-search-icon--emoji" aria-hidden>
              <span className="global-search-emoji">{TYPE_EMOJI[result.type]}</span>
            </div>
          }
          title={
            <Text strong className="global-search-title">
              <HighlightedText text={result.title} highlight={highlightQuery} variant="title" />
            </Text>
          }
          description={
            <div className="global-search-description">
              {result.subtitle && (
                <div>
                  <HighlightedText text={result.subtitle} highlight={highlightQuery} />
                </div>
              )}
              {result.description && (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  <HighlightedText text={result.description} highlight={highlightQuery} />
                </Text>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  const showHistoryPanel = !query.trim() && searchHistory.length > 0;
  const showEmptyHint = !query.trim() && searchHistory.length === 0;

  const dropdownContent = (
    <div
      className="global-search-dropdown-inner"
      style={{
        width: '400px',
        maxHeight: '400px',
        overflow: 'auto',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e8e8e8',
      }}
    >
      {showHistoryPanel && (
        <div className="global-search-history-block">
          <div className="global-search-history-header">
            <Space>
              <HistoryOutlined style={{ color: '#666' }} />
              <Text strong>История поиска</Text>
            </Space>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleClearHistory}
              className="global-search-clear-button"
            />
          </div>
          {searchHistory.map((item, index) => (
            <div
              key={`${item.query}-${item.timestamp}-${index}`}
              className="global-search-history-item"
              onClick={() => handleHistorySelect(item.query)}
            >
              <Space>
                <SearchOutlined style={{ color: '#999' }} />
                <Text className="global-search-history-query">{item.query}</Text>
                <Text type="secondary" className="global-search-history-count">
                  {item.resultCount} результатов
                </Text>
              </Space>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="global-search-loading">
          <Spin />
          <div style={{ marginTop: '8px' }}>Поиск...</div>
        </div>
      ) : searchError ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Text type="danger">Ошибка поиска</Text>
        </div>
      ) : hasActiveSearch && results.length > 0 ? (
        <div className="global-search-grouped">
          {groupedResults.map((section) => (
            <div key={section.title} className="global-search-section">
              <div className="global-search-section-header">
                <Text strong className="global-search-section-title">
                  {section.title}
                </Text>
              </div>
              <List dataSource={section.items} renderItem={(result) => renderSearchResultItem(result)} />
            </div>
          ))}
        </div>
      ) : hasActiveSearch ? (
        <Empty description="Ничего не найдено" className="global-search-empty" />
      ) : showEmptyHint ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Text type="secondary">Введите запрос для поиска по всем данным системы</Text>
          <div style={{ marginTop: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Используйте Ctrl+K для быстрого доступа
            </Text>
          </div>
          <div style={{ marginTop: '12px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              История поиска будет сохраняться автоматически
            </Text>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dropdown
      overlay={dropdownContent}
      trigger={['click']}
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
      overlayStyle={{
        padding: 0,
        backgroundColor: 'transparent',
      }}
      overlayClassName="global-search-dropdown"
    >
      <Input
        ref={inputRef}
        placeholder="Поиск по всем данным... (Ctrl+K)"
        prefix={<SearchOutlined />}
        value={query}
        onChange={handleQueryChange}
        onKeyDown={handleInputKeyDown}
        onFocus={() => setVisible(true)}
        style={{ width: '300px' }}
        allowClear
      />
    </Dropdown>
  );
};

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function HighlightedText({
  text,
  highlight,
  variant = 'default',
}: {
  text: string;
  highlight: string;
  variant?: 'default' | 'title';
}) {
  const h = highlight.trim();
  if (!h || !text) {
    return <>{text}</>;
  }

  const markClass =
    variant === 'title' ? 'global-search-highlight global-search-highlight--title' : 'global-search-highlight';

  try {
    const tokens = h.split(/\s+/).filter((t) => t.length > 0);
    if (tokens.length === 0) return <>{text}</>;

    const pattern = tokens
      .map(escapeRegExp)
      .sort((a, b) => b.length - a.length)
      .join('|');
    const re = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(re);

    return (
      <>
        {parts.map((part, i) => {
          const isMatch =
            part.length > 0 && tokens.some((t) => part.toLowerCase() === t.toLowerCase());
          return isMatch ? (
            <mark key={i} className={markClass}>
              {part}
            </mark>
          ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
          );
        })}
      </>
    );
  } catch {
    return <>{text}</>;
  }
}

export default GlobalSearch;
